/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { message } from "antd";
import { all_routes } from "../../../router/all_routes";
import {
  DisplayMode,
  EmployeeCardItem,
  OrgUnitPositionItem,
  ReportingLine,
  fetchEmployeeShortProfile,
  fetchManagerSelfServiceData,
  getAvatarDetails,
  getLoggedInEmpId,
  padEmpId,
} from "./managerSelfService.service";
import ApproverMssPanel from "./ApproverMssPanel";
import "./managerSelfService.scss";

interface HierarchyStep {
  empId: string;
  name: string;
}

const MSS_TABS: { key: DisplayMode; label: string }[] = [
  { key: "ORG_UNITS_AND_POSITIONS", label: "Org Units & Positions" },
  { key: "DIRECT_REPORTING", label: "Direct Reporting" },
  { key: "EMPLOYEES", label: "Employees" },
];

const na = (value: unknown) => {
  const text = String(value ?? "").trim();
  return text || "N/A";
};

const ManagerSelfService: React.FC = () => {
  const navigate = useNavigate();
  const initialEmpId = useMemo(() => getLoggedInEmpId(), []);

  const [viewMode, setViewMode] = useState<"team" | "approver">("team");
  const [displayMode, setDisplayMode] = useState<DisplayMode>("DIRECT_REPORTING");
  const [empId, setEmpId] = useState<string>(initialEmpId);
  const [inputEmpId, setInputEmpId] = useState<string>(initialEmpId);
  const [query, setQuery] = useState<string>("");
  const [debouncedQuery, setDebouncedQuery] = useState<string>("");

  // Shared across all three tabs, same as the app. Index 0 is the root manager.
  const [hierarchy, setHierarchy] = useState<HierarchyStep[]>([
    { empId: initialEmpId, name: "" },
  ]);
  const hierarchyRef = useRef<HierarchyStep[]>([{ empId: initialEmpId, name: "" }]);
  const pressedEmpIds = useRef<Set<string>>(new Set());
  const forceSyncRef = useRef(false);
  const requestSeq = useRef(0);

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reportingLine, setReportingLine] = useState<ReportingLine | null>(null);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [employees, setEmployees] = useState<EmployeeCardItem[]>([]);
  const [orgUnits, setOrgUnits] = useState<OrgUnitPositionItem[]>([]);
  // Selected item for Modal Detail View
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeCardItem | null>(null);
  const [selectedOrgUnit, setSelectedOrgUnit] = useState<OrgUnitPositionItem | null>(null);
  const [profileDetails, setProfileDetails] = useState<any>(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // Debounce search query input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(query);
    }, 400);
    return () => clearTimeout(handler);
  }, [query]);

  const replaceHierarchy = (next: HierarchyStep[]) => {
    hierarchyRef.current = next;
    setHierarchy(next);
  };

  // Fetch data on parameters change. A slower response for a previous
  // employee must not overwrite the employee that is now on screen.
  const loadData = useCallback(
    async (mode: DisplayMode, targetEmpId: string, search: string, forceSync = false) => {
      const seq = ++requestSeq.current;
      setLoading(true);
      setError(null);

      try {
        const result = await fetchManagerSelfServiceData({
          display: mode,
          empId: targetEmpId,
          query: search,
          forceSync,
          updateBy: forceSync ? initialEmpId : undefined,
        });
        if (seq !== requestSeq.current) return;

        // Same as the app: an Employees drill with nobody under them steps back.
        if (
          mode === "EMPLOYEES" &&
          result.sourceCount === 0 &&
          result.employees.length === 0 &&
          hierarchyRef.current.length > 1 &&
          padEmpId(hierarchyRef.current[hierarchyRef.current.length - 1]?.empId) === padEmpId(targetEmpId)
        ) {
          message.info("No subordinates found");
          const next = hierarchyRef.current.slice(0, -1);
          const parent = next[next.length - 1];
          replaceHierarchy(next);
          setEmpId(parent.empId);
          setInputEmpId(parent.empId);
          return;
        }

        setReportingLine(result.reportingLine);
        setTotalRecords(result.totalCount);
        setEmployees(result.employees);
        setOrgUnits(result.orgUnits);
        if (result.reportingLine?.name) {
          const current = hierarchyRef.current;
          const last = current[current.length - 1];
          if (
            last &&
            !last.name &&
            padEmpId(last.empId) === padEmpId(result.reportingLine.empId || targetEmpId)
          ) {
            const next = [...current];
            next[next.length - 1] = { ...last, name: result.reportingLine.name };
            replaceHierarchy(next);
          }
        }
      } catch (err: any) {
        if (seq !== requestSeq.current) return;
        const msg = err?.message || "Failed to load manager self-service records.";
        setError(msg);
        setReportingLine(null);
        setTotalRecords(0);
        setEmployees([]);
        setOrgUnits([]);
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    [initialEmpId]
  );

  useEffect(() => {
    if (viewMode !== "team") return;
    const forceSync = forceSyncRef.current;
    forceSyncRef.current = false;
    loadData(displayMode, empId, debouncedQuery, forceSync);
  }, [viewMode, displayMode, empId, debouncedQuery, loadData]);

  const resetHierarchy = (nextEmpId: string, name = "") => {
    replaceHierarchy([{ empId: nextEmpId, name }]);
  };

  // Handle Manager Emp ID submit from input
  const handleApplyEmpId = (e: React.FormEvent) => {
    e.preventDefault();
    const padded = padEmpId(inputEmpId);
    resetHierarchy(padded);
    setEmpId(padded);
    setInputEmpId(padded);
  };

  const handleResetEmpId = () => {
    const defaultId = getLoggedInEmpId();
    setInputEmpId(defaultId);
    setEmpId(defaultId);
    resetHierarchy(defaultId);
  };

  const openHierarchyAt = (index: number) => {
    const current = hierarchyRef.current;
    if (index < 0 || index >= current.length) return;
    const next = current.slice(0, index + 1);
    const target = next[next.length - 1];
    replaceHierarchy(next);
    setQuery("");
    if (padEmpId(target.empId) === padEmpId(empId)) return;
    setEmpId(target.empId);
    setInputEmpId(target.empId);
  };

  const handleDrillBack = () => {
    if (hierarchyRef.current.length <= 1) return;
    openHierarchyAt(hierarchyRef.current.length - 2);
  };

  // Employees arrow: move into that person's team. The shared trail is what
  // Back and the breadcrumb use, on every tab.
  const handleEmployeeArrow = (emp: EmployeeCardItem) => {
    const padded = padEmpId(emp.empId);
    if (!padded || padded === padEmpId(empId)) return;

    forceSyncRef.current = pressedEmpIds.current.has(padded);
    pressedEmpIds.current.add(padded);

    const current = [...hierarchyRef.current];
    const last = current[current.length - 1];
    if (last && !last.name && reportingLine?.name) {
      current[current.length - 1] = { ...last, name: reportingLine.name };
    }
    current.push({ empId: padded, name: emp.fullName || `Employee ${padded}` });
    replaceHierarchy(current);
    setQuery("");
    setEmpId(padded);
    setInputEmpId(padded);
  };

  const openEmployeeProfile = async (emp: EmployeeCardItem) => {
    setSelectedEmployee(emp);
    setProfileDetails(null);
    setProfileLoading(true);
    try {
      const details = await fetchEmployeeShortProfile(emp.empId);
      setProfileDetails(details);
    } catch {
      setProfileDetails(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const openLeaveOverview = (emp: EmployeeCardItem) => {
    navigate(all_routes.LeaveManagement.list, {
      state: {
        overrideEmployeeID: emp.empId,
        overrideEmployeeName: emp.fullName,
        fromMss: true,
      },
    });
  };

  const openCreateLeave = (emp: EmployeeCardItem) => {
    navigate(all_routes.LeaveManagement.create, {
      state: {
        overrideEmployeeID: emp.empId,
        overrideEmployeeName: emp.fullName,
        fromMss: true,
      },
    });
  };

  // Filtered lists for searching
  const filteredEmployees = useMemo(() => {
    if (!debouncedQuery) return employees;
    const lower = debouncedQuery.toLowerCase();
    return employees.filter(
      (emp) =>
        String(emp.fullName || "").toLowerCase().includes(lower) ||
        String(emp.empId || "").toLowerCase().includes(lower) ||
        String(emp.designation || "").toLowerCase().includes(lower) ||
        String(emp.department || "").toLowerCase().includes(lower) ||
        String(emp.email || "").toLowerCase().includes(lower) ||
        String(emp.phone || "").includes(lower)
    );
  }, [employees, debouncedQuery]);

  const filteredOrgUnits = useMemo(() => {
    if (!debouncedQuery) return orgUnits;
    const lower = debouncedQuery.toLowerCase();
    return orgUnits.filter(
      (ou) =>
        String(ou.orgUnitName || "").toLowerCase().includes(lower) ||
        String(ou.positionTitle || "").toLowerCase().includes(lower) ||
        String(ou.holderName || "").toLowerCase().includes(lower) ||
        String(ou.holderEmpId || "").includes(lower) ||
        String(ou.orgUnitId || "").toLowerCase().includes(lower)
    );
  }, [orgUnits, debouncedQuery]);

  return (
    <div className="page-wrapper mss-container">
      <div className="content">
        {/* Page Header & Breadcrumb */}
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1 d-flex align-items-center gap-2">
                  {hierarchy.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-link text-danger p-0"
                      title="Back to previous employee"
                      onClick={handleDrillBack}
                    >
                      <i className="ti ti-arrow-left" style={{ fontSize: 22 }} />
                    </button>
                  )}
                  Manager Self-Service Module
                </h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={all_routes.employeeDashboard}>Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="#">Other Services</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      {viewMode === "approver" ? "Approver" : "Manager Self-Service Module"}
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>

        <div className="mss-view-switch mb-3">
          <button
            type="button"
            className={viewMode === "team" ? "active" : ""}
            onClick={() => setViewMode("team")}
          >
            My Team
          </button>
          <button
            type="button"
            className={viewMode === "approver" ? "active" : ""}
            onClick={() => setViewMode("approver")}
          >
            Approver
          </button>
        </div>

        {viewMode === "approver" ? (
          <ApproverMssPanel />
        ) : (
        <>
        {/* Header Overview Card */}
        <div className="mss-header-card">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div>
              <div className="mss-header-title">
                <i className="ti ti-users-group"></i>
                Manager Self-Service Overview
              </div>
              <p className="mss-header-subtitle">
                {reportingLine?.name
                  ? `${reportingLine.name}${reportingLine.designation ? ` · ${reportingLine.designation}` : ""}`
                  : "Team reporting for the selected manager"}
                {" · "}
                <span className="fw-bold text-dark font-monospace">{reportingLine?.empId || empId}</span>
              </p>
            </div>
            <div className="d-flex align-items-center gap-2">
              <button
                className="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
                onClick={() => loadData(displayMode, empId, debouncedQuery, true)}
                disabled={loading}
              >
                <i className={`ti ti-refresh ${loading ? "fa-spin" : ""}`}></i>
                Refresh
              </button>
            </div>
          </div>

          {/* Stat Badges (Dynamically matches the selected dropdown mode) */}
          <div className="mss-stats-row">
            {displayMode === "EMPLOYEES" && (
              <>
                <div className="mss-stat-pill active-pill">
                  <span>All Subordinates</span>
                  <span className="mss-stat-count">{debouncedQuery ? filteredEmployees.length : totalRecords}</span>
                </div>
                <div className="mss-stat-pill">
                  <span>Active Status</span>
                  <span className="mss-stat-count bg-success">
                    {filteredEmployees.filter((e) => String(e.status || "").toLowerCase() === "active").length || filteredEmployees.length}
                  </span>
                </div>
              </>
            )}

            {displayMode === "DIRECT_REPORTING" && (
              <>
                <div className="mss-stat-pill active-pill">
                  <span>Direct Reports</span>
                  <span className="mss-stat-count">{debouncedQuery ? filteredEmployees.length : totalRecords}</span>
                </div>
                <div className="mss-stat-pill">
                  <span>Active Status</span>
                  <span className="mss-stat-count bg-success">
                    {filteredEmployees.filter((e) => String(e.status || "").toLowerCase() === "active").length || filteredEmployees.length}
                  </span>
                </div>
              </>
            )}

            {displayMode === "ORG_UNITS_AND_POSITIONS" && (
              <>
                <div className="mss-stat-pill active-pill">
                  <span>Org Units & Positions</span>
                  <span className="mss-stat-count">{debouncedQuery ? filteredOrgUnits.length : totalRecords}</span>
                </div>
                <div className="mss-stat-pill">
                  <span>Occupied Positions</span>
                  <span className="mss-stat-count bg-primary">
                    {filteredOrgUnits.filter((ou) => String(ou.status || "").toLowerCase() === "occupied").length}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {!loading && reportingLine && (
          <div className="mss-reporting-line">
            <div className="mss-reporting-line-label">Reporting Line</div>
            <div className="mss-reporting-line-body">
              <div
                className="mss-avatar"
                style={{
                  backgroundColor: getAvatarDetails(reportingLine.name, reportingLine.empId).bg,
                  color: getAvatarDetails(reportingLine.name, reportingLine.empId).text,
                }}
              >
                {getAvatarDetails(reportingLine.name, reportingLine.empId).initial}
              </div>
              <div className="mss-reporting-line-meta">
                <div className="mss-reporting-line-name">{reportingLine.name}</div>
                <div className="mss-reporting-line-sub">
                  <span className="font-monospace">{reportingLine.empId}</span>
                  {reportingLine.designation ? <span>{reportingLine.designation}</span> : null}
                </div>
                {(reportingLine.orgUnitName || reportingLine.orgUnitID) && (
                  <div className="mss-reporting-line-org">
                    <i className="ti ti-building"></i>
                    <span>{reportingLine.orgUnitName || "Org Unit"}</span>
                    {reportingLine.orgUnitID ? (
                      <span className="font-monospace">{reportingLine.orgUnitID}</span>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {hierarchy.length > 1 && (
          <div className="alert alert-info py-2 px-3 d-flex flex-column flex-md-row align-items-md-center justify-content-between gap-2 mb-3 rounded-3 border-0 bg-primary-subtle text-primary shadow-sm">
            <div className="d-flex align-items-center gap-2 flex-wrap small">
              <i className="ti ti-sitemap fs-5"></i>
              <span className="fw-semibold text-dark">Hierarchy:</span>
              {hierarchy.map((step, idx) => {
                const isCurrent = idx === hierarchy.length - 1;
                const label = `${step.name || "Employee"} (${step.empId})`;
                return (
                  <React.Fragment key={`${step.empId}-${idx}`}>
                    {idx > 0 && <i className="ti ti-chevron-right fs-6 text-muted"></i>}
                    {isCurrent ? (
                      <span className="fw-bold text-dark font-monospace bg-white px-2 py-1 rounded border">
                        {label}
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-decoration-none fw-bold text-primary"
                        onClick={() => openHierarchyAt(idx)}
                        title={`Open ${label}`}
                      >
                        {label}
                      </button>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary py-1 px-2 d-inline-flex align-items-center gap-1 flex-shrink-0"
              onClick={handleDrillBack}
            >
              <i className="ti ti-arrow-left"></i>
              Back
            </button>
          </div>
        )}

        <div className="d-flex flex-wrap gap-2 mb-3">
          {MSS_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className="btn"
              onClick={() => setDisplayMode(tab.key)}
              style={{
                borderRadius: 10,
                fontWeight: 600,
                background: displayMode === tab.key ? "#ab1f24" : "#f3f4f6",
                color: displayMode === tab.key ? "#fff" : "#111",
                border: "none",
                padding: "8px 16px",
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Error Banner */}
        {error && !loading && (
          <div className="alert alert-danger d-flex align-items-center justify-content-between mb-4" role="alert">
            <div className="d-flex align-items-center gap-2">
              <i className="ti ti-alert-circle fs-5"></i>
              <div>
                <strong>Error:</strong> {error}
              </div>
            </div>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => loadData(displayMode, empId, debouncedQuery)}
            >
              Retry
            </button>
          </div>
        )}

        {/* Loading State: Skeleton Cards */}
        {loading && (
          <div className="mss-cards-grid">
            {[1, 2, 3, 4, 5, 6].map((k) => (
              <div key={k} className="mss-skeleton-card">
                <div className="d-flex align-items-center gap-3">
                  <div className="skeleton-avatar"></div>
                  <div className="flex-1 d-flex flex-column gap-2" style={{ flex: 1 }}>
                    <div className="skeleton-line title"></div>
                    <div className="skeleton-line sub"></div>
                  </div>
                </div>
                <div className="skeleton-line full"></div>
                <div className="skeleton-line short"></div>
                <div className="skeleton-line full"></div>
              </div>
            ))}
          </div>
        )}

        {/* Content Display */}
        {!loading && !error && (
          <>
            <div className="text-uppercase fw-semibold text-muted mb-2" style={{ letterSpacing: "0.4px", fontSize: 13 }}>
              {displayMode === "ORG_UNITS_AND_POSITIONS"
                ? `Org Units & Positions (${filteredOrgUnits.length || totalRecords})`
                : displayMode === "DIRECT_REPORTING"
                ? `Direct Reports (${filteredEmployees.length || totalRecords})`
                : `Employees (${filteredEmployees.length || totalRecords})`}
            </div>

            {displayMode === "ORG_UNITS_AND_POSITIONS" ? (
              filteredOrgUnits.length > 0 ? (
                <div className="d-flex flex-column gap-3">
                  {filteredOrgUnits.map((ou, index) => {
                    const title = ou.positionTitle || ou.orgUnitName;
                    const objectId = ou.positionId && !String(ou.positionId).startsWith("POS-")
                      ? ou.positionId
                      : ou.orgUnitId;
                    const area = ou.raw?.PersonnelArea || ou.raw?.personnelArea;
                    const subarea = ou.raw?.Subarea || ou.raw?.PersonnelSubarea || ou.raw?.personnelSubarea;
                    return (
                      <div key={ou.id || index} className="card border-0 shadow-sm">
                        <div className="card-body d-flex align-items-center gap-3">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                            style={{ width: 56, height: 56, background: "#f3f4f6" }}
                          >
                            <i className="ti ti-users" style={{ fontSize: 26, color: "#6b7280" }} />
                          </div>
                          <div className="flex-grow-1">
                            <div className="fw-bold">{title}</div>
                            <div className="text-muted">{objectId}</div>
                            <div className="text-muted small">Personnel Area: {na(area)}</div>
                            <div className="text-muted small">Personnel Subarea: {na(subarea)}</div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-link text-danger p-0"
                            title="Short Profile"
                            onClick={() => setSelectedOrgUnit(ou)}
                          >
                            <i className="ti ti-info-circle" style={{ fontSize: 22 }} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-muted py-4">No data available</div>
              )
            ) : filteredEmployees.length > 0 ? (
              <div className="d-flex flex-column gap-3">
                {filteredEmployees.map((emp, index) => (
                    <div
                      key={emp.id || index}
                      className="card border-0 shadow-sm"
                      onClick={displayMode === "EMPLOYEES" ? () => handleEmployeeArrow(emp) : undefined}
                      style={displayMode === "EMPLOYEES" ? { cursor: "pointer" } : undefined}
                    >
                      <div className="card-body d-flex gap-3">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 fw-bold"
                          style={{
                            width: 56,
                            height: 56,
                            background: getAvatarDetails(emp.fullName, emp.empId).bg,
                            color: getAvatarDetails(emp.fullName, emp.empId).text,
                          }}
                        >
                          {getAvatarDetails(emp.fullName, emp.empId).initial}
                        </div>
                        <div className="flex-grow-1">
                          <div className="fw-bold">{emp.fullName}</div>
                          <div className="text-muted">{emp.empId}</div>
                          <div className="text-muted small">Position: {na(emp.positionTitle || emp.designation)}</div>
                          <div className="text-muted small">Org. Unit: {na(emp.orgUnitName || emp.department)}</div>
                          <div className="mt-2" style={{ color: "#ab1f24", fontWeight: 600 }}>
                            <button type="button" className="btn btn-link p-0" style={{ color: "#ab1f24" }} onClick={(event) => { event.stopPropagation(); openEmployeeProfile(emp); }}>
                              Short Profile
                            </button>
                            <span className="mx-1">|</span>
                            <button type="button" className="btn btn-link p-0" style={{ color: "#ab1f24" }} onClick={(event) => { event.stopPropagation(); openLeaveOverview(emp); }}>
                              Leave Overview
                            </button>
                            <span className="mx-1">|</span>
                            <button type="button" className="btn btn-link p-0" style={{ color: "#ab1f24" }} onClick={(event) => { event.stopPropagation(); openCreateLeave(emp); }}>
                              Create Leave Request
                            </button>
                          </div>
                          <button
                            type="button"
                            className="btn btn-link p-0 mt-1"
                            style={{ color: "#ab1f24", fontWeight: 600 }}
                            onClick={(event) => { event.stopPropagation(); message.info("No Action Assigned"); }}
                          >
                            Initiate Employee Processes
                          </button>
                        </div>
                        {displayMode === "EMPLOYEES" && (
                          <button
                            type="button"
                            className="btn btn-link text-muted p-0 align-self-center"
                            title="Subordinates"
                            onClick={(event) => { event.stopPropagation(); handleEmployeeArrow(emp); }}
                          >
                            <i className="ti ti-chevron-right" style={{ fontSize: 22 }} />
                          </button>
                        )}
                      </div>
                    </div>
                ))}
              </div>
            ) : (
              <div className="text-muted py-4">No data available</div>
            )}
          </>
        )}

        {selectedOrgUnit && (
          <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.45)" }} role="dialog">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                <div className="modal-header border-0">
                  <h4 className="modal-title fw-bold">Short Profile</h4>
                  <button type="button" className="btn-close" onClick={() => setSelectedOrgUnit(null)} />
                </div>
                <div className="modal-body pt-0">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: 56, height: 56, background: "#f3f4f6" }}>
                      <i className="ti ti-users" style={{ fontSize: 26 }} />
                    </div>
                    <div>
                      <div className="fw-bold">{selectedOrgUnit.positionTitle || selectedOrgUnit.orgUnitName}</div>
                      <div className="text-muted">{selectedOrgUnit.positionId || selectedOrgUnit.orgUnitId}</div>
                    </div>
                  </div>
                  {[
                    ["COMPANY CODE:", "N/A"],
                    ["BUSINESS AREA:", "N/A"],
                    ["PERSONNEL AREA:", na(selectedOrgUnit.raw?.PersonnelArea)],
                    ["PERSONNEL SUBAREA:", na(selectedOrgUnit.raw?.Subarea || selectedOrgUnit.raw?.PersonnelSubarea)],
                    ["CONTROLLING AREA:", "N/A"],
                    ["COST CENTER:", "N/A"],
                    ["EMPLOYEE GROUP:", na(selectedOrgUnit.raw?.empGroup)],
                    ["EMPLOYEE SUBGROUP:", na(selectedOrgUnit.raw?.cadre)],
                  ].map(([label, value]) => (
                    <div key={label} className="d-flex justify-content-between py-1">
                      <span className="fw-bold">{label}</span>
                      <span>{value}</span>
                    </div>
                  ))}
                  <div className="bg-light fw-bold px-2 py-2 my-2">VACANCY</div>
                  <div className="d-flex justify-content-between py-1"><span className="fw-bold">APPROVED (%):</span><span>N/A</span></div>
                  <div className="d-flex justify-content-between py-1"><span className="fw-bold">OCCUPIED (%):</span><span>N/A</span></div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="fw-bold">STAFFING STATUS:</span>
                    <span>{selectedOrgUnit.holderName && selectedOrgUnit.holderName !== "Vacant" ? "Occupied" : "Vacant"}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedEmployee && (
          <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.45)" }} role="dialog">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow">
                <div className="modal-header border-0">
                  <h4 className="modal-title fw-bold">Short Profile</h4>
                  <button type="button" className="btn-close" onClick={() => setSelectedEmployee(null)} />
                </div>
                <div className="modal-body pt-0">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
                      style={{
                        width: 56,
                        height: 56,
                        background: getAvatarDetails(selectedEmployee.fullName, selectedEmployee.empId).bg,
                        color: getAvatarDetails(selectedEmployee.fullName, selectedEmployee.empId).text,
                      }}
                    >
                      {getAvatarDetails(selectedEmployee.fullName, selectedEmployee.empId).initial}
                    </div>
                    <div>
                      <div className="fw-bold">{selectedEmployee.fullName}</div>
                      <div className="text-muted">{selectedEmployee.empId}</div>
                    </div>
                  </div>
                  <div className="bg-light fw-bold px-2 py-2 mb-2">CONTACT DETAILS</div>
                  {profileLoading ? (
                    <div className="text-muted py-2">Loading...</div>
                  ) : (
                    <>
                      <div className="d-flex justify-content-between py-1">
                        <span className="fw-bold">CELL PHONE NUMBER:</span>
                        <span>{na(profileDetails?.phone ?? profileDetails?.mobile)}</span>
                      </div>
                      <div className="d-flex justify-content-between py-1">
                        <span className="fw-bold">EMAIL:</span>
                        <span>{na(profileDetails?.email)}</span>
                      </div>
                    </>
                  )}
                  <div className="bg-light fw-bold px-2 py-2 my-2">ORGANIZATION DETAILS</div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="fw-bold">ORGANIZATIONAL UNIT:</span>
                    <span className="text-end">{na(profileDetails?.sap_department_designation_wings?.department_name || selectedEmployee.orgUnitName || selectedEmployee.department)}</span>
                  </div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="fw-bold">POSITION:</span>
                    <span>{na(selectedEmployee.positionTitle || selectedEmployee.designation || profileDetails?.sap_department_designation_wings?.designation_name)}</span>
                  </div>
                  <div className="d-flex justify-content-between py-1">
                    <span className="fw-bold">EMPLOYEE SUBGROUP:</span>
                    <span>{na(profileDetails?.pay_scale || selectedEmployee.grade)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        </>
        )}

      </div>
    </div>
  );
};

export default ManagerSelfService;
